import { useEffect, useRef, useState } from "react";
import type { CoachTaskRecord, CompetencyCode, CompetencyLevel, GrowthRoute, PointRulesResponse } from "@cross-border-agent/contracts";
import "./beginner-copy.css";

const competencyCopy: Record<CompetencyCode, { title: string; practice: string; preparation: string }> = {
  business_strategy: { title: "想清楚怎么起步", practice: "确定卖给谁、能投入多少预算和时间。", preparation: "已保存起步资料" },
  platform_operation: { title: "选择开店平台", practice: "了解平台要求，选一个适合自己的平台。", preparation: "已选择起步平台" },
  store_setup: { title: "准备并注册店铺", practice: "核对注册条件，准备资料并跟进审核。", preparation: "已有首次注册任务记录" },
  product_selection: { title: "判断商品适不适合卖", practice: "检查需求、卖点和运输条件，筛掉不合适的商品。", preparation: "已有首次商品任务记录" },
  profit_management: { title: "算清一件商品赚多少", practice: "把进货、运费和平台费用算进成本。", preparation: "已有首次利润核算记录" },
  supplier_management: { title: "找货源、问清采购条件", practice: "向供应商确认价格、起订量、质量和发货时间。", preparation: "已有首次供应商核对记录" },
  listing_operation: { title: "准备商品上架", practice: "整理标题、图片和属性，在发布前逐项检查。", preparation: "已有首次上架任务记录" },
  risk_compliance: { title: "避开禁售和侵权风险", practice: "核对禁限售、品牌授权和商品资质要求。", preparation: "已有首次商品检查记录" },
  independent_execution: { title: "换个商品再做一遍", practice: "重新练习选品、算利润和准备上架。", preparation: "已有起步记录" },
  fulfillment_service: { title: "把第一单发出去", practice: "准备运费模板，按平台时效发货并回填单号。", preparation: "已有首次发货记录" },
  customer_service: { title: "回复买家并处理售后", practice: "回答买家常见问题，按流程处理退货和纠纷。", preparation: "已有首次售后记录" },
  traffic_growth: { title: "算清能不能投广告", practice: "先算保本 ROAS，再用小预算跑完整观察周期。", preparation: "已有首次投放测算记录" },
};

const practiceLevelCopy: Record<CompetencyLevel, string> = {
  L0: "还没开始", L1: "已有起步记录", L2: "已再次练习", L3: "已积累多次练习",
};

export function GrowthPracticeProgress({ competencies }: { competencies: GrowthRoute["competencies"] }) {
  return <section className="beginner-practice" aria-labelledby="practice-heading">
    <header className="section-heading"><span>我的练习进度</span><h2 id="practice-heading">开店要用到的 9 件事，慢慢练会</h2></header>
    <p className="beginner-lead">这是练习记录，不是能力认证；完成任务后逐步更新。</p>
    <div className="beginner-practice-grid">{competencies.map((item) => {
      const copy = competencyCopy[item.code];
      return <article key={item.code}>
        <span className={`practice-status is-${item.level}`}>{item.level === "L1" ? copy.preparation : practiceLevelCopy[item.level]}</span>
        <h3>{copy.title}</h3><p>{copy.practice}</p>
        <small>{item.evidenceCount > 0 ? `已保存 ${item.evidenceCount} 项相关记录` : "还没有相关记录，可从对应的路线步骤开始"}</small>
      </article>;
    })}</div>
    <details className="beginner-explainer"><summary>这些进度是怎么记录的？</summary>
      <p>明确确认起步建议与平台选择，或把已完成的任务关联到成长路线后，这里会留下相应记录。仅保存资料不会通过评估。一个任务可能同时更新选品、利润或上架等几项练习，数量不代表开了几家店或卖出了几件商品。</p>
      <p>“已再次练习”表示已关联后续商品练习任务；“已积累多次练习”表示已关联至少 3 个后续商品练习任务。保存资料和积累任务记录，都不能单独说明你已掌握独立经营。</p>
    </details>
  </section>;
}

export const starterGoals = [
  { id: "registration", title: "我想注册第一家店", page: "打开你想入驻的平台卖家注册页", prompt: "我想在这个平台注册第一家店。请先帮我看注册条件和需要的资料，再告诉我现在这一步怎么做。" },
  { id: "selection", title: "我想知道这个商品能不能卖", page: "打开你正在看的商品或货源页", prompt: "我第一次做跨境电商，想判断当前这个商品适不适合卖。请帮我检查卖点、运输和可能的风险，缺什么信息先问我。" },
  { id: "listing", title: "我想上架第一个商品", page: "打开商品详情页或店铺的商品发布页", prompt: "我想把这个商品上架到我的店铺。请先核对商品资料，再帮我准备标题、图片和属性，告诉我发布前还要检查什么。" },
] as const;

export async function copyStarterGoal(prompt: string, writeText: (text: string) => Promise<void>) {
  try {
    await writeText(prompt);
    return "已复制。回到电商网页，打开小满插件，粘贴后发送。";
  } catch {
    return "复制没有成功。请选中上面这段话手动复制，再粘贴到小满插件。";
  }
}

const taskLifecycleLabel: Record<CoachTaskRecord["lifecycle"], string> = {
  active: "进行中", completed: "已完成", archived: "已收起",
};
const stepStatusLabel = { completed: "已完成", current: "正在做", pending: "还没开始", waiting_user: "等你操作", waiting_external: "等待平台结果" };
const formatDate = (value: string) => new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));

function CoachHistory({ tasks }: { tasks: CoachTaskRecord[] }) {
  const scrolledHash = useRef("");
  useEffect(() => {
    let frame = 0;
    const scrollToTask = () => {
      cancelAnimationFrame(frame);
      const hash = location.hash;
      if (!hash.startsWith("#task-")) { scrolledHash.current = ""; return; }
      if (scrolledHash.current === hash) return;
      frame = requestAnimationFrame(() => {
        const target = document.getElementById(hash.slice(1));
        if (target) { target.scrollIntoView({ block: "start" }); scrolledHash.current = hash; }
      });
    };
    scrollToTask();
    window.addEventListener("hashchange", scrollToTask);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("hashchange", scrollToTask); };
  }, [tasks]);
  return <section className="beginner-history" aria-labelledby="coach-history-heading">
    <header className="section-heading"><span>我的任务记录</span><h2 id="coach-history-heading">做到哪一步，回来都能查看</h2></header>
    {tasks.length === 0 ? <div className="beginner-history-empty"><h3>你还没有保存的任务</h3><p>先选上面一个目标，到小满插件里发送。创建的注册或商品上架任务会显示在这里；普通问答不一定生成任务记录。</p></div> : <>
      <p className="beginner-lead">有 {tasks.filter(record => record.lifecycle === "active").length} 个进行中、{tasks.filter(record => record.lifecycle === "completed").length} 个已完成，共 {tasks.length} 个任务。继续最新任务，请回到电商网页打开插件。</p>
      <div className="beginner-history-list">{tasks.map((record) => {
        const task = record.task;
        const title = task.taskType === "platform_registration" ? task.title : `${task.productTitle} · ${task.destinationPlatform}`;
        const currentStep = task.taskType === "platform_registration" ? task.steps.find(step => step.id === task.currentStepId) : task.steps.find(step => step.status === "current");
        const progress = task.steps.filter(step => step.status === "completed").length;
        return <article id={`task-${encodeURIComponent(record.id)}`} key={record.id} className={`beginner-task-card is-${record.lifecycle}`}>
          <header><div><span>{task.taskType === "platform_registration" ? "注册店铺" : "商品准备与上架"}</span><h3>{title}</h3></div><span className="beginner-task-status">{taskLifecycleLabel[record.lifecycle]}</span></header>
          <p>{task.summary}</p><div className="beginner-task-progress">已完成 <strong>{progress} / {task.steps.length}</strong> 步</div>
          {record.lifecycle === "active" && <section className="task-resume-guide"><h4>继续这一份任务</h4><ol><li>{task.taskType === "platform_registration" ? `回到 ${task.platform} 对应站点的卖家注册或审核页面。` : `回到这个商品的详情页，或 ${task.destinationPlatform} 商品发布页。`}</li><li>打开小满插件，使用当前同一账户登录。</li><li>点击顶部“任务”，再点“查看任务并继续”。核对任务标题与本页一致，不用重新发送“开始任务”。</li></ol>{task.status === "waiting_external" && <p>目前在等待平台结果。先查看审核状态；没有结果时不要重复提交，可以整理后续需要的商品资料。</p>}<a href="/extension">查看插件安装和打开方法</a></section>}
          <dl>
            <div><dt>{record.lifecycle === "active" ? "现在做到" : "最后记录"}</dt><dd>{currentStep?.title || (record.lifecycle === "completed" ? "任务已完成" : record.lifecycle === "archived" ? "任务已收起，可查看已保存的步骤" : "待继续，请在插件查看任务")}</dd></div>
            <div><dt>下一步提示</dt><dd>{task.nextAction}</dd></div>
            <div><dt>更新于</dt><dd>{formatDate(record.updatedAt)}</dd></div>
          </dl>
          {record.lifecycle === "archived" && <p className="beginner-task-note">开始新任务后，之前的任务会收起保留；“已收起”不代表完成。</p>}
          <details><summary>查看全部步骤</summary><ol className="beginner-task-steps">{task.steps.map(step => <li key={step.id}><span>{stepStatusLabel[step.status]}</span><strong>{step.title}</strong><p>{step.description}</p></li>)}</ol></details>
          {record.result && <details><summary>查看已保存的结果</summary><p>{record.result.summary}</p>{record.result.artifacts.length > 0 && <ul>{record.result.artifacts.map(artifact => <li key={artifact.id}><strong>{artifact.title}</strong>：{artifact.summary}</li>)}</ul>}{record.result.review?.notes.map(note => <p key={note}>{note}</p>)}</details>}
        </article>;
      })}</div>
    </>}
  </section>;
}

export function BeginnerCoachPage({ rules, tasks }: { rules: PointRulesResponse; tasks: CoachTaskRecord[] }) {
  const [copyMessage, setCopyMessage] = useState<{ id: string; text: string } | null>(null);
  const copy = async (id: string, prompt: string) => setCopyMessage({ id, text: await copyStarterGoal(prompt, text => navigator.clipboard.writeText(text)) });
  return <main className="page beginner-coach-page">
    <header className="page-heading"><span className="folio">04</span><div><p>跟着小满做</p><h1>下一步不会做？<br />打开小满，一步一步来。</h1></div></header>
    <section className="beginner-coach-start" aria-labelledby="coach-start-heading">
      <div className="beginner-start-heading"><div><span>第一次使用，从这 3 步开始</span><h2 id="coach-start-heading">先打开你要处理的电商网页</h2></div><a href="/extension">还没安装？查看安装步骤</a></div>
      <ol className="beginner-start-steps">
        <li><b>1</b><h3>在网页右侧打开小满</h3><p>打开卖家注册页、商品详情页或商品发布页，点击浏览器工具栏里的“小满陪跑”插件。</p></li>
        <li><b>2</b><h3>说清楚现在想做什么</h3><p>比如“帮我看这个商品适不适合卖”。不知道怎么说，可以复制下面的例子。</p></li>
        <li><b>3</b><h3>跟着提示，完成当前一步</h3><p>按提示补充资料、检查页面或确认填写。遇到不懂的内容，直接问“这一步是什么意思”。</p></li>
      </ol>
      <p className="beginner-start-note">实际操作在浏览器插件里进行；这个页面帮你了解怎么开始，并查看已保存的任务。</p>
    </section>
    <section className="beginner-goals" aria-labelledby="starter-goals-heading">
      <header className="section-heading"><span>选一件现在想做的事</span><h2 id="starter-goals-heading">复制一句话，发给小满</h2></header>
      <div className="beginner-goal-grid">{starterGoals.map(goal => <article key={goal.id}><span>{goal.page}</span><h3>{goal.title}</h3><p>{goal.prompt}</p><button type="button" onClick={() => void copy(goal.id, goal.prompt)}>复制这句话</button>{copyMessage?.id === goal.id && <small role="status">{copyMessage.text}</small>}</article>)}</div>
    </section>
    <CoachHistory tasks={tasks} />
    <div className="beginner-coach-notes">
      <section><h2>哪些操作需要你来确认？</h2><p>小满可以解释页面、准备草稿，并在你确认后填写支持的字段。最终提交、发布、支付和删除由你操作；密码、验证码和支付信息也需要你自己输入。</p></section>
      <section><h2>怎么扣积分？</h2><p>复制例句和查看本页记录不扣积分。在插件中使用 AI 分析、检索或生成内容时，按对应操作扣分；深度模式每次额外 {rules.deepModeExtra} 积分。未完成的请求会退还预扣积分。</p><details className="beginner-explainer"><summary>查看每次操作的积分</summary><dl className="beginner-point-rules">{rules.operations.map(rule => <div key={rule.route}><dt>{rule.name}</dt><dd>{rule.points} 积分</dd></div>)}</dl></details></section>
    </div>
  </main>;
}
